import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex } from '@chakra-ui/react'
import { ProductType } from '@leid/api-products'
import { GetServerSideProps, GetServerSidePropsResult, NextPage } from 'next'
import Router, { useRouter } from 'next/router'
import { v4 } from 'uuid'

import { productsApi } from 'api/products/products'
import { Item, SortingInfo } from 'appTypes/mapper'
import { Metadata } from 'appTypes/shared'
import { SearchForProducts } from 'components/form/SearchForProducts'
import { SEO } from 'components/helpers/SEO'
import { BrowseItemsWithAdditionalElements, CategoriesCards, ProductBundles } from 'components/partials/browse'
import { SendProductRequest } from 'components/partials/browse/SendProductRequest/indes'
import SortButtonCoordinates from 'components/partials/browse/SortButton'
import { NoResults } from 'components/partials/search'
import { BrowseTopBar, FeaturedBundle, Pagination } from 'components/partials/shared'
import { Loader } from 'components/ui/Loader'
import { SectionHeading } from 'components/ui/Text/SectionHeading'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { Container } from 'styles/shared'
import { getSortingPointCoordinatesInfo } from 'utils/apiUtils/products'
import { STORAGE_KEYS } from 'utils/constants'
import { tryParseAsNumber } from 'utils/parser'
import { parseRouterQuery, parseRouterQueryAsNumber } from 'utils/parseRouterQuery'
import { getFromStorage, saveToStorage } from 'utils/storage'

import { getItems } from './api/products/items'

type Props = {
  items: Item[]
  metadata: Metadata
  sortingInfo: SortingInfo | null
}

const Browse: NextPage<Props> = ({ items, metadata, sortingInfo }) => {
  const { t } = useTranslation(['common', 'pageTitles'])
  const router = useRouter()
  const { customerInfo } = useCustomerInfo()

  const [isLoading, setIsLoading] = useState(false)
  const [areSearchResultsDisplayed, setAreSearchResultsDisplayed] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [bundles, setBundles] = useState<null | Item[]>(null)

  const startLoading = () => {
    setIsLoading(true)
  }
  const stopLoading = () => {
    setIsLoading(false)
  }

  const getFeaturedBundles = async (category: string) => {
    const {
      data: { items: featuredBundles },
    } = await productsApi.getFeaturedBundles(category)

    setBundles(featuredBundles)
  }

  useEffect(() => {
    const category = parseRouterQuery(router.query.category) || ''
    const searchTerm = parseRouterQuery(router.query.searchTerm) || ''
    const locationId = parseRouterQueryAsNumber(router.query.location)
    const managerId = parseRouterQueryAsNumber(router.query.manager)

    if (searchTerm) {
      let userToken: string | undefined = undefined
      if (!customerInfo.customer) {
        userToken = JSON.parse(getFromStorage(STORAGE_KEYS.userToken))
        if (!userToken) {
          userToken = v4()
          saveToStorage(STORAGE_KEYS.userToken, userToken)
        }
      }

      apiClient.sendSearchEvent({
        userId: customerInfo.customer?.id ?? undefined,
        userToken: userToken,
        searchTerm: searchTerm,
        results: items.length !== 0,
      })
    }

    if ((searchTerm || locationId || managerId) && !category) {
      setAreSearchResultsDisplayed(true)
    } else {
      setAreSearchResultsDisplayed(false)
    }

    getFeaturedBundles(category)
    setSearchTerm(searchTerm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query])

  useEffect(() => {
    Router.events?.on('routeChangeStart', startLoading)
    Router.events?.on('routeChangeComplete', stopLoading)

    return () => {
      Router.events?.off('routeChangeStart', startLoading)
      Router.events?.off('routeChangeComplete', stopLoading)
    }
  }, [])

  const onPageChange = (page: number) => {
    router.query.page = page.toString()
    router.push(router)
  }

  return (
    <>
      <SEO title={`Leid | ${t('pageTitles:browse')}`} />
      <BrowseTopBar redirectTo="browse" />
      <Box display={{ md: 'none' }} marginTop="20px">
        <Container>
          <Flex marginBottom="36px" justifyContent="space-between" alignItems="center">
            <SectionHeading>{t('browseProducts')}</SectionHeading>
            {/* <SortButton redirectTo="browse" /> */}
          </Flex>
          <SearchForProducts redirectTo="browse" />
        </Container>
      </Box>
      <CategoriesCards redirectTo="browse" />
      <Box minHeight="57vh">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Container>
              <Flex direction="row" justifyContent="flex-end" marginTop="20px" wordBreak="break-word">
                <SortButtonCoordinates sortingInfo={sortingInfo} />
              </Flex>
            </Container>
            {items.length === 0 ? (
              <Container>
                <NoResults />
                <Box maxWidth={{ sm: '500px' }} marginX="auto" minHeight="65vh">
                  <SendProductRequest searchTerm={searchTerm} />
                </Box>
              </Container>
            ) : (
              <>
                <Box display={{ base: 'none', lg: 'block' }}>
                  <BrowseItemsWithAdditionalElements itemsCountBreak={12} products={items} articles={null}>
                    {!areSearchResultsDisplayed && bundles && bundles.length >= 1 && (
                      <Container>
                        <FeaturedBundle bundle={bundles[0]} />
                      </Container>
                    )}
                  </BrowseItemsWithAdditionalElements>
                </Box>

                <Box display={{ lg: 'none' }}>
                  <BrowseItemsWithAdditionalElements itemsCountBreak={4} products={items} articles={null}>
                    {!areSearchResultsDisplayed && bundles && bundles.length >= 1 && (
                      <ProductBundles bundles={bundles} />
                    )}
                  </BrowseItemsWithAdditionalElements>
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {metadata?.last > 1 && (
        <Box marginBottom="32px" display={!isLoading ? 'block' : 'none'}>
          <Pagination
            onPageChange={(selectedItem) => {
              onPageChange(selectedItem.selected + 1)
              window.scrollTo(0, 0)
            }}
            pageCount={metadata.last}
            currentPage={metadata.page}
          />
        </Box>
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}): Promise<GetServerSidePropsResult<Props>> => {
  const page = tryParseAsNumber(query.page) ?? 1
  const locationId = tryParseAsNumber(query.location)
  const managerId = tryParseAsNumber(query.manager)
  const searchTerm = query.searchTerm?.toString()
  const category = query.category?.toString()

  const sortingInfo = await getSortingPointCoordinatesInfo(req)

  const apiResponse = await getItems({
    page,
    items: 36,
    locationId,
    managerId,
    query: searchTerm,
    category,
    product_type: ProductType.Product,
    complementary: false,
    active: true,
    lat: sortingInfo?.lat,
    long: sortingInfo?.long,
  })

  return {
    props: {
      items: JSON.parse(JSON.stringify(apiResponse?.items ?? [])),
      metadata: JSON.parse(JSON.stringify(apiResponse?.metadata ?? null)),
      sortingInfo: JSON.parse(JSON.stringify(sortingInfo ?? null)),
    },
  }
}

export default Browse
